import { Inject, Injectable, OnDestroy, Renderer2, RendererFactory2, signal, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, filter, map, merge, of, shareReplay, takeUntil, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
// controls global functions in the app, e.g. imprint or privacy modal
export class GlobalService implements OnDestroy {
    private readonly _destroying$ = new Subject<void>();
    private readonly _renderer: Renderer2 | undefined;
    public imprintOpen = signal(false);
    public privacyOpen = signal(false);
    // supplies current url
    public currentUrl$: Observable<string> = merge(
        of(this._router.url),
        this._router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects),
        ),
    ).pipe(shareReplay(1));

    constructor(
        private readonly _router: Router,
        private readonly _rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) private readonly _document: Document,
    ) {
        this._renderer = this._rendererFactory.createRenderer(null, null);

        // stores unsubscribe method of renderer listener
        let _fullscreenListener: (() => void) | undefined;
        // listen to changes in fullscreen mode
        this._isFullscreen$
            .pipe(
                takeUntil(this._destroying$),
                tap((enterFullscreen) => {
                    if (enterFullscreen) {
                        // request fullscreen in browser API
                        this._document.documentElement.requestFullscreen({ navigationUI: 'show' }).catch((err: any) => {
                            console.error(
                                `An error occurred while trying to switch into fullscreen mode: ${err.message} (${err.name})`,
                            );
                        });

                        // listen to changes once fullscreen is active, set state once user leaves mode
                        _fullscreenListener = this._renderer?.listen('window', 'fullscreenchange', () => {
                            if (!this._document.fullscreenElement) {
                                this.toggleFullscreen(false);
                            }
                        });
                    } else if (_fullscreenListener) {
                        // if no fullscreen is active, we can remove the eventListener on window
                        // by calling the stored unlistener method
                        this._document.exitFullscreen();
                        _fullscreenListener();
                    }
                }),
            )
            .subscribe();
    }

    public toggleImprint(forcedState?: boolean) {
        this.imprintOpen.set(forcedState ?? !this.imprintOpen());
    }

    public togglePrivacy(forcedState?: boolean) {
        this.privacyOpen.set(forcedState ?? !this.privacyOpen());
    }

    // takes care of fullscreen mode in app
    private readonly _isFullscreen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public isFullscreen$: Observable<boolean> = this._isFullscreen$.asObservable();

    // callable without any params to toggle when no state is known, state can be forced
    public toggleFullscreen(forcedState?: boolean) {
        const value = forcedState ?? !this._isFullscreen$.getValue();
        this._isFullscreen$.next(value);
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}
